import Image from 'next/legacy/image'
import useTranslation from 'next-translate/useTranslation'
import {
  Col,
  Form,
  Loader,
  Row,
  Uploader,
} from 'rsuite'
import LegacyImageIcon from '@rsuite/icons/legacy/Image'
import { Dispatch, MutableRefObject, SetStateAction } from 'react'
import { FileType } from 'rsuite/esm/Uploader'
import { UploaderInstance } from 'rsuite/esm/Uploader/Uploader'

import { getSiteCode, useAuth } from '../../../services/useAuth'
import { imageLoader } from '../../../utils/images'
import { cookies } from '../../../utils/fetcher'
import { MEDIA_TYPE_SUPPLIER_IMAGE } from '../../hooks/useSupplierProfileData'

import styles from '../../../styles/VibeIconUploader.module.less'

const apiUrl = process.env.NEXT_PUBLIC_API_URL

interface VibeIconUploaderProps {
  vibeIconFileInfo: string | null
  vibeIconUploaderRef: MutableRefObject<UploaderInstance | null>
  setVibeIconImageData: Dispatch<SetStateAction<FileType | null>>
  setVibeIconFileInfo: Dispatch<SetStateAction<string | null>>
  setDirty: Dispatch<SetStateAction<boolean>>
  isUploadingVibeIcon: boolean
  supplierId: number
  widthForMdScreen?: number
}

const VibeIconUploader: React.FC<VibeIconUploaderProps> = (props) => {
  const {
    vibeIconFileInfo,
    vibeIconUploaderRef,
    setVibeIconImageData,
    setVibeIconFileInfo,
    setDirty,
    isUploadingVibeIcon,
    supplierId,
    widthForMdScreen,
  } = props

  const { t } = useTranslation('profile')
  const { user } = useAuth()

  const code = getSiteCode(user)

  const previewFile = (file: File, callback: (s?: string) => void) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      callback(reader.result?.toString())
    }
    if (file) reader.readAsDataURL(file)
  }

  const handleVibeIconImageChange = (fileList: FileType[]) => {
    const imageFile = fileList.at(-1) // Last file is the latest image selected

    if (!imageFile?.blobFile) return

    setDirty(true)
    setVibeIconImageData(imageFile)
    previewFile(imageFile.blobFile, (value?: string) => setVibeIconFileInfo(value || ''))
  }

  const vibeIconMediaData = [{
    'media.siteid': user?.siteid,
    'media.type': MEDIA_TYPE_SUPPLIER_IMAGE,
    'media.label': 'vibe-icon',
    'media.domain': 'supplier',
    'media.languageid': null,
  }]

  return (
    <Row className="margin-top-spacer-double">
      <Col
        xs={24}
        md={widthForMdScreen}
      >
        <Form.Group>
          <Form.ControlLabel>
            {t('Supplier Vibe Icon')}
          </Form.ControlLabel>
          <div
            className={`${styles['uploader-row-flex']} ${styles['vibe-icon-uploader']}`}
            data-testid="supplier-vibe-icon-uploader"
          >
            <Uploader
              fileListVisible={false}
              listType="picture"
              action={`${apiUrl}/api/v1/${code}/upload/supplier/${supplierId}`}
              headers={{
                'X-XSRF-TOKEN': cookies('XSRF-TOKEN'),
              }}
              autoUpload={false}
              onChange={handleVibeIconImageChange}
              name="0"
              data={{
                data: JSON.stringify(vibeIconMediaData),
              }}
              ref={vibeIconUploaderRef}
              withCredentials
              draggable
            >
              <button type="button">
                {isUploadingVibeIcon && (
                <Loader
                  backdrop
                  center
                />
                )}
                {vibeIconFileInfo ? (
                  <Image
                    loader={imageLoader}
                    src={vibeIconFileInfo ?? ''}
                    alt="vibe icon"
                    className="vibe-icon-img"
                    layout="responsive"
                    width={120}
                    height={80}
                  />
                ) : (
                  <div className={styles['vibe-icon-placeholder']}>
                    <LegacyImageIcon className="icon-size-double" />
                    <p>{t('Upload Vibe Icon')}</p>
                  </div>
                )}
              </button>
            </Uploader>
            <p className={styles['vibe-icon-helper-text']}>
              {t('Upload your vibe icon to enhance your brand presence across the platform')}
            </p>
          </div>
        </Form.Group>
      </Col>
    </Row>
  )
}

export default VibeIconUploader
